<script>
  import { createEventDispatcher } from "svelte";
  import { range, max, rollup } from "d3-array";
  import { playing, time } from "../stores/stores.js";
  import wordmark from "../svg/pudding/wordmark.svg";
  import Icon from "./helpers/Icon.svelte";
  export let data;

  const dispatch = createEventDispatcher();

  const flat = [].concat(...data.map((d) => d.captions));

  const colors = [
    "rgba(221, 221, 221, 0.6)",
    "rgba(175, 240, 91, 0.6)",
    "rgba(82, 246, 103, 0.6)",
    "rgba(29, 223, 163, 0.6)",
    "rgba(35, 171, 216, 0.6)",
    "rgba(76, 110, 219, 0.6)",
    "rgba(110, 64, 170, 0.6)",
  ];

  // ticks
  const maxVal = max(data, (d) => d.count);
  const digits = `${maxVal}`.length;
  const mult = Math.pow(10, digits - 1);
  const ticks = range(Math.floor(maxVal / mult)).map((d) => (d + 1) * mult);

  // season guides
  const seasons = Array.from(
    rollup(
      data,
      (v) => v.length,
      (d) => +d.id.split("e")[0].replace("s", "")
    )
  );

  let shouldSlide = false;

  $: currentCaption = flat.find((d) => $time < d.offset + d.duration) || {};
  $: currentI = currentCaption.i;
  $: slide = $playing ? currentCaption.index > data.length / 2 : shouldSlide;
</script>

<div class="chart" class:slide>
  <button
    on:click="{() => {
      shouldSlide = false;
    }}"
    class="prev"><Icon strokeWidth="3px" name="chevron-left" /></button>
  <button
    on:click="{() => {
      shouldSlide = true;
    }}"
    class="next"><Icon strokeWidth="3px" name="chevron-right" /></button>

  <div class="axis-y">
    {#each ticks as tick}
      <div class="tick" style="bottom: {(tick / maxVal) * 100}%;">
        <p>{tick}</p>
      </div>
    {/each}
  </div>

  <div class="axis-x">
    {#each seasons as [number, count]}
      <div class="season" style="width: {(count / data.length) * 100}%;">
        <p>S{number}</p>
      </div>
    {/each}
  </div>

  {#each data as episode}
    <div class="episode">
      {#each episode.captions as caption}
        {#each caption.conseq as c}
          <div
            on:click="{() => dispatch('seek', caption.offset)}"
            class:playing="{$playing}"
            class:active="{caption.i === currentI}"
            class="sequence sequence--{c}"
            style="color: {colors[Math.min(c, 7) - 1]};">
            {#each range(c) as i}<span></span>{/each}
          </div>
        {/each}
      {/each}
    </div>
  {/each}

  <!-- <figure>
    <img src="assets/abeds.jpg" alt="baby abeds" />
    <figcaption>Baby Abeds with 9 consecutive "cools."</figcaption>
  </figure> -->
</div>

<div class="bottom">
  <div class="legend">
    <p>consecutive cools</p>
    <ul>
      {#each colors as c, i}
        <li style="background-color: {c};">
          {i === colors.length - 1 ? `${i + 1}+` : i + 1}
        </li>
      {/each}
    </ul>
  </div>
  <div class="wordmark">
    <a href="https://pudding.cool">{@html wordmark}</a>
  </div>
</div>

<style>
  .chart {
    display: flex;
    position: relative;
    /* min-width: 35em; */
    width: 199%;
    transition: transform 0.5s ease-out;
  }

  .chart.slide {
    transform: translateX(-50%);
  }

  .episode {
    display: flex;
    min-height: 1em;
    flex-direction: column-reverse;
    width: calc(100% / 108);
    margin: 0 0.5px;
  }

  .sequence {
    display: flex;
    flex-direction: column;
    position: relative;
  }

  .playing.active.sequence {
    outline: 1px solid var(--fg);
  }

  .playing span {
    opacity: 0.5;
    outline: none;
    transition: opacity 0.5s ease-in-out;
  }

  span {
    display: block;
    width: 100%;
    position: relative;
    background-color: currentColor;
    height: 0.3em;
    margin: 0.5px 0;
    outline: 1px solid var(--fg);
  }

  .axis-y {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    font-size: 0.75em;
  }

  .tick {
    position: absolute;
    left: 0;
    width: 100%;
    height: 1px;
    border-bottom: 1px dashed var(--gray-light);
  }

  .tick p {
    margin: 0;
    display: inline-block;
    transform: translate(0%, 0%);
  }

  .tick:first-of-type p {
    display: none;
  }

  .axis-x {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    transform: translateY(125%);
    font-size: 0.75em;
  }

  .season {
    color: var(--black);
  }

  .season p {
    margin: 0;
    text-align: center;
    color: currentColor;
    border-top: 2px solid currentColor;
    margin: 0 0.5px;
    padding: 0 0.5px;
    font-weight: 600;
  }

  .season:nth-of-type(even) {
    color: var(--gray);
  }

  .bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 4em;
    padding-bottom: 1em;
  }

  .legend {
    width: 14em;
    /* text-align: center; */
    font-size: 0.75em;
    text-transform: uppercase;
  }

  .legend p {
    margin: 0.25em 0;
  }

  .legend ul {
    display: flex;
    justify-content: center;
    list-style-type: none;
  }

  .legend li {
    width: calc(100% / 7);
    height: 1.5em;
    outline: 1px solid var(--fg);
    margin: 0.5px;
    line-height: 1.5em;
    text-align: center;
  }

  .wordmark {
    width: 7em;
    transform: translateY(15px);
  }

  .wordmark a {
    border: none;
    color: var(--fg);
  }

  .wordmark a:hover {
    background-color: transparent;
  }

  button {
    position: absolute;
    top: 100%;
    line-height: 1;
    padding: 0.25em;
    font-size: 1.25em;
    z-index: var(--z-top);
    transform: translateY(75%);
  }

  button.prev {
    left: 50%;
    margin-left: 0.25em;
  }

  button.next {
    margin-right: 0.25em;
    right: 50%;
  }

  @media only screen and (min-width: 370px) {
    .wordmark {
      width: 9em;
    }

    button {
      top: 0;
      transform: translateY(50%);
    }
    .bottom {
      margin-top: 2.5em;
    }
  }

  @media only screen and (min-width: 640px) {
    button {
      display: none;
    }

    .chart {
      width: 100%;
    }

    .chart.slide {
      transform: translateX(0);
    }

    .tick p {
      transform: translate(-125%, -50%);
    }

    .tick:first-of-type p {
      display: block;
    }

    span {
      padding-top: 100%;
      height: auto;
    }

    .sequence {
      cursor: pointer;
    }

    .sequence:hover {
      outline: 2px solid var(--fg);
    }

    .playing.active.sequence {
      outline: 2px solid var(--fg);
    }
  }
</style>
